import _ums from "@cached/um.json";
// Formato del risultato di un test per unità di misura, condiviso con il PHP (MSP_Formatter::risultato).
import FORMATO_RISULTATO from "@config/commons/formato_risultato.json";
// Unità di convertUpu, condivise con il PHP (MSP_Formatter::convertUpu).
import CONVERSIONI_UM from "@config/commons/conversioni_um.json";
// Separatori di decimali e migliaia, condivisi con il PHP (MSP_Formatter::number).
import FORMATO_NUMERO from "@config/commons/formato_numero.json";

const EMPTY_VALUE = "-";
// Separatori in uso: formato_numero.json, salvo impostaFormatoNumero()
let formatoNumero = FORMATO_NUMERO;

export default class MSPFormatter {
  /**
   * Sostituisce i separatori di formato_numero.json (stessa struttura, sezione `valuta` compresa);
   * senza argomento torna alla configurazione. Serve ai test, per non dipendere dalla configurazione in uso.
   * @param {Object|null} formato
   */
  static impostaFormatoNumero(formato = null) {
    formatoNumero = formato ?? FORMATO_NUMERO;
  }

  static value(value, options = {}) {
    if (this.empty(value)) return this.emptyValue(options);

    if (options.precision != null) {
      if (options.formatted !== false) return this.number(value, { precision: options.precision });

      const n = parseFloat(value);
      return isNaN(n) ? value : this.arrotonda(n, options.precision);
    }
    return value;
  }

  static empty(value) {
    const empties = ["", "-", "/", EMPTY_VALUE];
    return empties.includes(value) || value == null || Number.isNaN(value);
  }

  static emptyValue(options) {
    const defaults = { formatted: true, empty_value: EMPTY_VALUE };
    options = { ...defaults, ...options };
    return options.formatted ? options.empty_value : 0;
  }

  static suffixFromUmCodice(umCodice) {
    const um = _ums.find((um) => um.codice === umCodice);
    return um?.suffix ?? null;
  }

  static factorFromUmCodice(umCodice) {
    const um = _ums.find((um) => um.codice === umCodice);
    return um ? um.conversione : 1;
  }

  static suffix(options) {
    const defaults = { has_suffix: true };
    options = { ...defaults, ...options };

    if (!options.has_suffix) return "";

    let suffix = options.suffix_um_codice
      ? this.suffixFromUmCodice(options.suffix_um_codice)
      : options.suffix;

    return suffix ?? "";
  }

  /**
   * Passo da una velocità: secondi per unità di distanza, formattati con sec2hms_auto.
   * @param {Number} value velocità in m/s (o già il passo in secondi con `is_mps: false`)
   * @param {String} umPasso unità del passo per convertUpu ("spk", "sp100m")
   * @param {Object} options `is_mps`, `formatted`, `suffix_um_codice`, `empty_value`
   */
  static passo(value, umPasso, options) {
    if (this.empty(value)) return this.emptyValue(options);

    // Velocità 0 → passo infinito; un passo di 0 secondi non è un valore
    const converted = (options.is_mps) ? this.convertUpu(value, "mps", umPasso) : +value;
    if (!Number.isFinite(converted) || converted <= 0) return this.emptyValue(options);

    if (!options.formatted) return converted;

    const suffix = this.suffix(options);
    return this.concatenate([this.sec2hms_auto(converted, options), suffix]);
  }

  /** Passo al km (05:00 /km) da una velocità in m/s. */
  static passoKm(value, options = {}) {
    const defaults = { formatted: true, is_mps: true, suffix_um_codice: "minkm" };
    return this.passo(value, "spk", { ...defaults, ...options });
  }

  /** Passo ai 100 m (01:30 /100mt) da una velocità in m/s. */
  static passo100mt(value, options = {}) {
    const defaults = { formatted: true, is_mps: true, suffix_um_codice: "min100mt" };
    return this.passo(value, "sp100m", { ...defaults, ...options });
  }

  /** Velocità in km/h (30,0 km/h) da una velocità in m/s. Usato da ciclismo e sci (fields.json). */
  static velocitaCiclismo(value, options = {}) {
    const defaults = { precision: 1, formatted: true, suffix_um_codice: "kmh" };
    options = { ...defaults, ...options };

    if (this.empty(value) || !Number.isFinite(+value)) return this.emptyValue(options);

    const kmh = this.convertUpu(+value, "mps", "kph");

    if (!options.formatted) {
      return this.arrotonda(kmh, options.precision);
    }

    return this.decorate(kmh, options);
  }

  static multiply(value, options = {}) {
    const defaults = { factor: 1, formatted: true };
    options = { ...defaults, ...options };

    if (this.empty(value) || !Number.isFinite(+value)) return this.emptyValue(options);

    const factor =
      options.factor_um_codice
        ? this.factorFromUmCodice(options.factor_um_codice)
        : options.factor;

    const multiplied = value * factor;

    if (!options.formatted) return multiplied;

    const suffix = this.suffix(options);
    return this.concatenate([this.number(multiplied, { precision: options.precision ?? 2 }), suffix]);
  }

  /**
   * Scompone una durata in secondi: unica regola di arrotondamento per tutte le durate.
   * Arrotonda al secondo (al decimo con `decimi`): 239,999976 (precisione di FormulaParser) → 4:00, non 3:59.
   * @param {Number|String} secondi
   * @param {Object} options `decimi`: arrotonda al decimo di secondo
   * @return {Object|null} {segno, h, m, s, d, minuti} con `minuti` = minuti totali; null se non è un numero finito
   */
  static scomponiDurata(secondi, options = {}) {
    const n = +secondi;
    if (secondi === null || secondi === "" || !Number.isFinite(n)) return null;

    const decimiTotali = options.decimi ? Math.round(Math.abs(n) * 10) : Math.round(Math.abs(n)) * 10;
    const interi = Math.floor(decimiTotali / 10);

    return {
      segno: n < 0 && decimiTotali > 0 ? "-" : "",
      h: Math.floor(interi / 3600),
      m: Math.floor((interi % 3600) / 60),
      s: interi % 60,
      d: decimiTotali % 10,
      minuti: Math.floor(interi / 60),
    };
  }

  /** Durata scomposta per `formatted: false`: {h, m, s}, con i decimi nei secondi e il segno su ogni parte. */
  static durataRaw(parti) {
    const segno = parti.segno ? -1 : 1;
    return {
      h: segno * parti.h || 0,
      m: segno * parti.m || 0,
      s: segno * (parti.s + parti.d / 10) || 0,
    };
  }

  /**
   * Formatta una durata in secondi secondo un pattern: H ore, i minuti, s secondi.
   * Senza H nel pattern i minuti sono quelli totali (5425 s con "i:s" → 90:25).
   * @param {Number} secs
   * @param {String} format es. "H:i:s", "i:s", "H:i"
   * @param {Object} options `decimi`: aggiunge ",d" ai secondi
   */
  static formatTime(secs, format = "H:i:s", options = {}) {
    const parti = this.scomponiDurata(secs, options);
    if (!parti) return this.emptyValue(options);

    const pad = n => n.toString().padStart(2, "0");
    const hasOre = format.includes("H");
    const valori = {
      H: parti.h,
      i: pad(hasOre ? parti.m : parti.minuti),
      s: pad(parti.s) + (options.decimi ? `,${parti.d}` : ""),
    };

    return parti.segno + format.replace(/[His]/g, token => valori[token]);
  }

  /**
   * Durata in secondi. `format`: "HMS" (1:30:25), "MS" (minuti totali: 90:25), "HM" (1:30).
   * @param {Number|String} value
   * @param {Object} options `format`, `decimi`, `formatted`, `empty_value`
   */
  static sec2hms(value, options = {}) {
    const defaults = { format: "HMS", formatted: true };
    options = { ...defaults, ...options };

    if (this.empty(value)) return this.emptyValue(options);

    const parti = this.scomponiDurata(value, options);
    if (!parti) return this.emptyValue(options);
    if (!options.formatted) return this.durataRaw(parti);

    const formati = { HMS: "H:i:s", MS: "i:s", HM: "H:i" };
    return this.formatTime(value, formati[options.format.toUpperCase()] ?? formati.HMS, options);
  }

  /** Durata in secondi: "i:s" sotto l'ora, "H:i:s" da un'ora in su (dopo l'arrotondamento: 3599,6 → 1:00:00). */
  static sec2hms_auto(seconds, options = {}) {
    options = { formatted: true, ...options };

    if (this.empty(seconds)) return this.emptyValue(options);

    const parti = this.scomponiDurata(seconds, options);
    if (!parti) return this.emptyValue(options);
    if (!options.formatted) return this.durataRaw(parti);

    return this.formatTime(seconds, parti.h > 0 ? "H:i:s" : "i:s", options);
  }

  /** Durata compatta: 1h30'20", 30'20", 45", 5,3" con `decimi`; con le ore i minuti restano anche se zero (1h0'20"). */
  static sec2hms_compact(seconds, options = {}) {
    options = { formatted: true, ...options };

    if (this.empty(seconds)) return this.emptyValue(options);

    const parti = this.scomponiDurata(seconds, options);
    if (!parti) return this.emptyValue(options);
    if (!options.formatted) return this.durataRaw(parti);

    const { h, m, s, d } = parti;
    let result = options.decimi ? `${s},${d}"` : (s > 0 ? `${s}"` : "");
    if (m > 0 || (h > 0 && result)) result = `${m}'${result}`;
    if (h > 0) result = `${h}h${result}`;
    return result && parti.segno + result;
  }

  static mt2km(value, options = {}) {
    const defaults = { precision: 2, formatted: true, suffix_um_codice: "km" };
    options = { ...defaults, ...options };

    if (this.empty(value) || !Number.isFinite(+value)) return this.emptyValue(options);

    const km = value / 1000;

    if (!options.formatted) {
      return this.arrotonda(km, options.precision);
    }

    const suffix = this.suffix(options);
    return this.concatenate([this.number(km, { precision: options.precision }), suffix]);
  }

  /**
   * Risultato di un test o valore di una zona nella sua unità di misura (src/config/commons/formato_risultato.json).
   * Zero, vuoti e non numerici → `empty_value`.
   * @param {Number|String} value valore salvato (passi in s/m)
   * @param {String} umCodice codice di um.json
   * @param {Object} options `has_suffix`, `formatted`, `empty_value`
   */
  static risultato(value, umCodice, options = {}) {
    options = { has_suffix: true, formatted: true, ...options };

    const um = _ums.find((um) => um.codice === umCodice);
    if (!um || this.empty(value) || !Number.isFinite(+value) || +value === 0) return this.emptyValue(options);

    const formato = FORMATO_RISULTATO.per_um[umCodice]
      ?? (um.um_tipo_id === FORMATO_RISULTATO.um_tipo_tempo ? FORMATO_RISULTATO.tempo : FORMATO_RISULTATO.default);
    const convertito = +value * (formato.fattore ?? 1);
    if (!options.formatted) return convertito;

    const testo = (formato.formatter === "number")
      ? this.number(convertito, { precision: um.decimali ?? 0 })
      : this[formato.formatter](convertito, { format: formato.format });

    return this.concatenate([testo, options.has_suffix ? um.suffix : ""]);
  }

  /**
   * Distanza in metri mostrata nell'unità scelta (km con 12,3 km, mt con 12.345 mt).
   * @param {Number|String} value metri
   * @param {Object} options `um_codice` ("mt" | "km"), `precision`, `has_suffix`, `formatted`
   */
  static distanza(value, options = {}) {
    options = { um_codice: "mt", precision: 0, has_suffix: true, formatted: true, ...options };

    if (this.empty(value) || !Number.isFinite(+value)) return this.emptyValue(options);

    const convertito = this.arrotonda(+value / this.factorFromUmCodice(options.um_codice), options.precision);
    if (!options.formatted) return convertito;

    const suffix = options.has_suffix ? this.suffixFromUmCodice(options.um_codice) : "";
    return this.concatenate([this.number(convertito, { precision: options.precision }), suffix]);
  }

  /**
   * Numero con fino a `precision` decimali, senza zeri finali (30 km/h, non 30,0), come MSP_Formatter::number.
   * Separatori da src/config/commons/formato_numero.json, sovrascrivibili con `decimale`/`migliaia`.
   */
  static number(value, options = {}) {
    const precision = options.precision ?? 0;
    const decimale = options.decimale ?? formatoNumero.decimale;
    const migliaia = options.migliaia ?? formatoNumero.migliaia;

    if (this.empty(value)) return this.emptyValue(options);

    const n = parseFloat(value);
    if (isNaN(n)) return value;

    // Cifre senza separatori ("-1234.5"), poi i separatori della configurazione
    const cifre = n.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
      useGrouping: false,
    });
    if (cifre === "-0") return "0";

    return this.separatori(cifre, decimale, migliaia);
  }

  /**
   * Prezzo con tutti i decimali, zeri finali compresi (30.00, 1234.50), come MSP_Formatter::valuta.
   * Separatori e decimali dalla sezione `valuta` di src/config/commons/formato_numero.json,
   * sovrascrivibili con `decimale`/`migliaia`/`decimali`. Il simbolo della valuta lo aggiunge chi chiama.
   * Solo per mostrare: i valori per i pagamenti (es. final_price per PayPal) restano numeri.
   */
  static valuta(value, options = {}) {
    const decimali = options.decimali ?? formatoNumero.valuta.decimali;
    const decimale = options.decimale ?? formatoNumero.valuta.decimale;
    const migliaia = options.migliaia ?? formatoNumero.valuta.migliaia;

    if (this.empty(value)) return this.emptyValue(options);

    const n = parseFloat(value);
    if (isNaN(n)) return value;

    const cifre = this.arrotonda(n, decimali).toLocaleString("en-US", {
      minimumFractionDigits: decimali,
      maximumFractionDigits: decimali,
      useGrouping: false,
    });
    // "-0.00" (es. -0,001 arrotondato) diventa "0.00"
    return this.separatori(cifre.replace(/^-(?=[0.]+$)/, ""), decimale, migliaia);
  }

  /** Applica i separatori a cifre senza separatori con il punto decimale ("-1234.5"). */
  static separatori(cifre, decimale, migliaia) {
    const [intero, decimali] = cifre.split(".");
    const testo = migliaia ? intero.replace(/\B(?=(\d{3})+(?!\d))/g, migliaia) : intero;
    return decimali ? testo + decimale + decimali : testo;
  }

  /**
   * Arrotonda a `precision` decimali come round() del PHP: metà lontano da zero (-2,5 → -3)
   * e sul valore decimale, non sulla sua approssimazione binaria (1,005 → 1,01, non 1).
   * @param {number|string} n convertito in numero come fa Math.round ("49.00" → 49, null → 0)
   * @param {number} precision decimali (default 0)
   * @returns {number}
   */
  static arrotonda(n, precision = 0) {
    n = Number(n);
    if (!Number.isFinite(n)) return n;
    // La notazione esponenziale sposta la virgola sulla stringa: "1.005e2" vale esattamente 100,5, 1.005 * 100 vale 100,4999…
    const arrotondato = Number(Math.round(Math.abs(n) + "e" + precision) + "e-" + precision);
    // String(n) già esponenziale (|n| < 1e-6 o ≥ 1e21) non si può estendere: arrotondamento diretto
    const valore = Number.isNaN(arrotondato) ? Math.round(Math.abs(n) * 10 ** precision) / 10 ** precision : arrotondato;
    return Math.sign(n) * valore || 0;
  }

  static concatenate(strings, options = {}) {
    const defaults = { sep: " " };
    options = { ...defaults, ...options };
    return strings.filter(s => s !== "" && s != null && s !== false).join(options.sep);
  }

  static decorate(value, options = {}) {
    const defaults = { suffix: "", prefix: "", sep: " ", formatted: true };
    options = { ...defaults, ...options };

    if (this.empty(value)) return this.emptyValue(options);

    let decoratedValue = this.value(value, options);
    // Senza `precision` value() restituisce il numero: al massimo 2 decimali, come nel PHP
    if (options.precision == null && Number.isFinite(+decoratedValue)) {
      decoratedValue = this.number(decoratedValue, { precision: 2 });
    }
    const suffix = this.suffix(options);

    return this.concatenate([options.prefix, decoratedValue, suffix], { sep: options.sep });
  }

  /**
   * Distanza in metri: km da 1000 m in su (sempre con `force_km`), altrimenti mt senza decimali;
   * `convert_units: false` resta in mt (gemello di MSP_Formatter::stringDistanza).
   * @param {Number|String} value metri
   * @param {Object} options `convert_units`, `force_km`, `precision` (km), `has_suffix`, `empty_value`
   */
  static stringDistanza(value, options = {}) {
    options = { convert_units: true, formatted: true, ...options };

    if (this.empty(value) || !Number.isFinite(+value)) return this.emptyValue(options);

    const isKm = options.force_km || (options.convert_units && +value >= 1000);
    if (isKm) {
      const km = this.arrotonda(+value / 1000, 2);
      return this.decorate(km, { ...options, suffix_um_codice: "km" });
    }
    return this.decorate(+value, { ...options, precision: 0, suffix_um_codice: "mt" });
  }

  /**
   * Valore formattato con una config `{formatter, options}` (es. le colonne della tabella lap);
   * formatter sconosciuto → valore invariato.
   */
  static byConfig(value, config) {
    if (typeof this[config?.formatter] !== "function") return value;
    return this[config.formatter](value, { ...(config.options ?? {}) });
  }

  static speed2pace(value, options = {}) {
    return this.invert(value, options);
  }

  static pace2speed(value, options = {}) {
    return this.invert(value, options);
  }

  static invert(value, options = {}) {
    options = { formatted: true, ...options };

    if (this.empty(value)) return this.emptyValue(options);
    return 1 / value;
  }

  /**
   * Converte fra unità semplici o composte (src/config/commons/conversioni_um.json, condiviso con il PHP).
   * Fra dimensioni diverse inverte il valore (mps → spk). Unità sconosciuta → NaN.
   * @param {Number} val
   * @param {String} um1 es. "mps", "km", "mt", "kmh"
   * @param {String} um2
   */
  static convertUpu(val, um1, um2) {
    const { separatori, unita, composte } = CONVERSIONI_UM;

    const canonica = (um) => Object.keys(unita).find((k) => unita[k].alias.includes(um)) ?? null;
    const separatore = new RegExp(separatori.map((s) => s.replace(/[.*+?^${}()|[\]\\/]/g, "\\$&")).join("|"));

    const split = (um) => {
      const espressione = composte[um] ?? um;
      const parts = espressione.split(separatore);
      return parts.length === 2 ? parts.map(canonica) : [canonica(espressione)];
    };

    const parti1 = split(um1);
    const parti2 = split(um2);
    if (parti1.includes(null) || parti2.includes(null)) return NaN;

    let [a0, a1] = parti1;
    let [b0, b1] = parti2;

    const flip = unita[a0].dimensione !== unita[b0].dimensione;
    if (flip) [b0, b1] = [b1, b0];

    const fattore = (um) => unita[um]?.fattore;
    const c0 = fattore(a0) / fattore(b0);
    const c1 = a1 && b1 ? fattore(a1) / fattore(b1) : 1;
    const result = val * c0 / c1;

    return flip ? 1 / result : result;
  }

  static raw(method, ...args) {
    if (typeof this[method] !== "function") {
      throw new Error(`Metodo formatter '${method}' non trovato`);
    }

    const lastArg = args[args.length - 1];
    if (typeof lastArg === "object" && !Array.isArray(lastArg)) {
      args[args.length - 1] = { ...lastArg, formatted: false };
    } else {
      args.push({ formatted: false });
    }

    return this[method](...args);
  }
}

